import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { getAuthHeaders } from '../lib/api';
import { PASS_THROUGH_COSTS } from '../lib/billing-copy';
import { useAuth } from '../contexts/AuthContext';

function isSubscribed(status) {
  return status === 'active' || status === 'trialing';
}

/**
 * Onboarding step: subscribe before A2P so pass-through fees bill to Stripe.
 */
const SubscribeOnboarding = ({ onComplete, checkoutContext = 'onboarding' }) => {
  const { userProfile, reloadProfile, profileReady } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subscribed = isSubscribed(userProfile?.subscription_status);

  useEffect(() => {
    if (profileReady && subscribed) {
      onComplete?.();
    }
  }, [profileReady, subscribed, onComplete]);

  async function handleSubscribe() {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: checkoutContext }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  async function handleRefreshStatus() {
    setLoading(true);
    setError('');
    try {
      await reloadProfile?.();
    } finally {
      setLoading(false);
    }
  }

  if (subscribed) {
    return (
      <div className="max-w-md mx-auto text-center p-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Subscription active</h2>
        <p className="text-muted-foreground mb-4">Continuing to AI Compliance…</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="CreditCard" size={32} className="text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Subscribe to continue</h2>
      <p className="text-muted-foreground text-center mb-6 text-sm">
        A subscription is required before AI Compliance. Pass-through carrier and Twilio costs are billed on your Stripe invoice — SMS is metered; A2P and phone fees are separate line items.
      </p>

      <div className="bg-muted/50 rounded-xl p-5 mb-6">
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-2xl font-bold text-foreground">$297</span>
          <span className="text-sm text-muted-foreground">/ month platform</span>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Plus usage &amp; pass-through costs on your Stripe invoice:</p>
        <ul className="space-y-1.5 text-xs text-muted-foreground mb-4">
          {PASS_THROUGH_COSTS.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="text-muted-foreground/70 mt-0.5">•</span>
              {item}
            </li>
          ))}
        </ul>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {[
            'AI-generated evergreen touchpoints',
            'Automated SMS delivery',
            'A2P 10DLC compliance registration',
            'Local area code number matching',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Icon name="Check" size={14} className="text-green-500 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <Button onClick={handleSubscribe} loading={loading} className="w-full mb-3">
        Subscribe with Stripe
      </Button>
      <Button variant="outline" onClick={handleRefreshStatus} disabled={loading} className="w-full">
        I already subscribed — refresh status
      </Button>
      <p className="text-xs text-muted-foreground text-center mt-4">Secured by Stripe. Cancel anytime from Settings.</p>
    </div>
  );
};

export default SubscribeOnboarding;
